import { useEffect, useState, type ReactElement } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../store/db';
import { useCuentaVinculada } from '../store/hooks';
import { activarPush, entornoPush, leerPush, type EstadoPushServidor } from '../store/push';
import {
  estadoDispositivo,
  ofrecerEnHoy,
  posponerAvisos,
  TEXTO_ESTADO,
} from './notificaciones-texto';

/**
 * QUE SELF TE AVISE (spec 2026-10-22). El permiso del sistema se pide aquí, en Hoy,
 * y solo al pulsar «Activar avisos»: la tarjeta cuenta antes qué va a llegar, para
 * que el «sí» del sistema sea una decisión y no un susto. «Ahora no» la calla una
 * semana. Con el permiso bloqueado no aparece: el sistema no volvería a preguntar.
 */

const CLAVE_POSPUESTO = 'avisosPospuestosHasta';

/** Lo que se ve en la mini-bandeja: dos avisos de verdad, con sus textos de verdad. */
const MUESTRA = [
  { titulo: 'Nivel 5', cuerpo: 'Subiste de nivel. Mira lo que llevas.' },
  { titulo: '25 min de foco', cuerpo: 'Escribir · Registrado.' },
] as const;

export function OfrecerAvisos(): ReactElement | null {
  const cuenta = useCuentaVinculada();
  const pospuesto = useLiveQuery(async () => {
    const fila = await db.meta.get(CLAVE_POSPUESTO);
    return typeof fila?.value === 'number' ? fila.value : null;
  });
  const [servidor, setServidor] = useState<EstadoPushServidor | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [resultado, setResultado] = useState<'activadas' | 'bloqueadas' | 'error' | null>(null);
  const [oculta, setOculta] = useState(false);
  // La hora de la visita: «Ahora no» se compara con ella, no con cada repintado.
  const [abiertaEn] = useState(() => Date.now());

  useEffect(() => {
    if (!cuenta) return;
    let vivo = true;
    leerPush()
      .then((e) => {
        if (vivo) setServidor(e);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cuenta]);

  // El «listo» se queda un momento y se va solo.
  useEffect(() => {
    if (resultado !== 'activadas') return;
    const t = setTimeout(() => setOculta(true), 3200);
    return () => clearTimeout(t);
  }, [resultado]);

  if (oculta || !cuenta || !servidor || pospuesto === undefined) return null;

  const estado = estadoDispositivo({
    ...entornoPush(),
    conCuenta: true,
    disponible: servidor.disponible,
    suscrito: servidor.suscrito,
  });
  const oferta = resultado === 'activadas' ? 'activar' : ofrecerEnHoy(estado, pospuesto, abiertaEn);
  if (!oferta) return null;

  const posponer = () =>
    void db.meta.put({ key: CLAVE_POSPUESTO, value: posponerAvisos(Date.now()) });

  const activar = () => {
    if (!servidor.clavePublica) return;
    setTrabajando(true);
    activarPush(servidor.clavePublica)
      .then((r) => setResultado(r))
      .catch(() => setResultado('error'))
      .finally(() => setTrabajando(false));
  };

  const listo = resultado === 'activadas';

  return (
    <section
      className={`hoy-avisos glass-1${listo ? ' hoy-avisos--listo' : ''}`}
      aria-labelledby="hoy-avisos-titulo"
    >
      <div className="hoy-avisos__bandeja" aria-hidden="true">
        {MUESTRA.map((m, i) => (
          <div
            key={m.titulo}
            className="hoy-avisos__notif"
            style={{ animationDelay: `${String(180 + i * 220)}ms` }}
          >
            <img className="hoy-avisos__icono" src="/icons/icon-192.png" alt="" />
            <span className="hoy-avisos__texto">
              <span className="hoy-avisos__app">selflevel · ahora</span>
              <span className="t-sm t-body-strong">{m.titulo}</span>
              <span className="t-sm t-dim">{m.cuerpo}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="hoy-avisos__cuerpo">
        <h2 id="hoy-avisos-titulo" className="t-title-3">
          {listo ? 'Listo. Self te avisará' : 'Que Self te avise'}
        </h2>
        {oferta === 'instalar' ? (
          <p className="t-sm t-dim">{TEXTO_ESTADO.instalar}</p>
        ) : listo ? (
          <p className="t-sm t-dim">Lo ajustas cuando quieras en Ajustes → Notificaciones.</p>
        ) : (
          <ul className="hoy-avisos__lista t-sm">
            <li>Cuando termine tu foco, aunque tengas la app cerrada.</li>
            <li>Cuando subas de nivel, y cuando tu rewind del año esté listo.</li>
            <li>Si el día sigue en blanco: como mucho uno cada tres horas.</li>
          </ul>
        )}
        {resultado === 'bloqueadas' ? (
          <p className="t-sm" role="alert">
            {TEXTO_ESTADO.bloqueadas}
          </p>
        ) : resultado === 'error' ? (
          <p className="t-sm" role="alert">
            No se pudo activar. Vuelve a intentarlo con conexión.
          </p>
        ) : null}

        {listo ? null : (
          <div className="hoy-avisos__acciones">
            {oferta === 'activar' ? (
              <button
                type="button"
                className="btn btn--primary"
                disabled={trabajando}
                onClick={activar}
              >
                {trabajando ? 'Activando…' : 'Activar avisos'}
              </button>
            ) : null}
            <button type="button" className="btn btn--ghost" onClick={posponer}>
              Ahora no
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
